import axios from 'axios';
import store from '@/store';
import login from './login';

// Contador de tentativas de refresh token
let refreshTokenAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 2;

const api = axios.create({
  baseURL: process.env.API_BASE_URL || 'https://api.videotecaead.com.br/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  config => {
    // Para FormData, o browser precisa definir automaticamente o Content-Type
    // com boundary. Se manter application/json, o backend recebe "{}".
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers?.set) {
        config.headers.set('Content-Type', undefined);
      } else if (config.headers) {
        delete config.headers['Content-Type'];
      }
    }

    const token = store.state.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => {
    // Resetar contador de tentativas quando login for bem-sucedido
    if (response.config.url.includes('/auth/login')) {
      refreshTokenAttempts = 0;
      return response;
    }
    // Resetar contador quando qualquer requisição for bem-sucedida após refresh
    if (refreshTokenAttempts > 0) {
      refreshTokenAttempts = 0;
    }
    return response;
  },
  async error => {
    const status = error.response ? error.response.status : null;
    const originalRequest = error.config;
    const requestUrl = String(originalRequest?.url || '');
    const requestMethod = String(originalRequest?.method || '').toLowerCase();
    const responseMessage = String(error.response?.data?.message || '').toLowerCase();

    const isBusinessErrorWithoutLogout = (() => {
      if (!(status === 400 || status === 403)) return false;
      return /\/files\/[^/]+\/cancel(?:\?|$)/i.test(requestUrl);
    })();

    const isCreateUserAlreadyExistsError = (() => {
      if (status !== 400 && status !== 403) return false;
      if (requestMethod !== 'post') return false;
      if (!/\/users(?:\?|$)/i.test(requestUrl)) return false;

      return responseMessage.includes('member already exists') ||
             responseMessage.includes('already exists') ||
             responseMessage.includes('já existe') ||
             responseMessage.includes('ja existe') ||
             responseMessage.includes('já cadastrado') ||
             responseMessage.includes('ja cadastrado');
    })();

    const isCreateUserBusinessErrorWithoutLogout = (() => {
      if (status !== 400 && status !== 403) return false;
      if (requestMethod !== 'post') return false;
      if (!/\/users(?:\?|$)/i.test(requestUrl)) return false;

      return true;
    })();

    if (originalRequest.url.includes('/auth/login') || 
        originalRequest.url.includes('/auth/refresh') || 
        originalRequest.url.includes('/auth/renew-refresh')) {
        return Promise.reject(error);
    }

    try {
      switch(status) {
        case 401:
          const errorMessage = error.response?.data?.message;
          
          if (errorMessage === "Token inválido") {
            if (!originalRequest._retry) {
              originalRequest._retry = true; // Marca a requisição para não tentar novamente
              
              // Verifica se já ultrapassou o limite de tentativas
              if (refreshTokenAttempts >= MAX_REFRESH_ATTEMPTS) {
                console.warn('Limite de tentativas de refresh token atingido. Fazendo logout...');
                logout();
                return Promise.reject(error);
              }
              
              const refreshToken = store.state.refreshToken; // Corrigido para acessar o store correto

              if (!refreshToken) {
                logout();
                return Promise.reject(error); // Rejeita se não houver refresh token
              }

              // Incrementa o contador de tentativas
              refreshTokenAttempts++;

              try {
                const response = await login.refreshToken(refreshToken); // Chama a função de refresh
                
                const { accessToken, refreshToken: newRefreshToken } = response.data;

                store.dispatch('storeToken', { token: accessToken }); // Corrigido para usar a action correta
                store.dispatch('storeRefreshToken', { refreshToken: newRefreshToken }); // Corrigido para usar a action correta

                // Resetar contador quando refresh funcionar
                refreshTokenAttempts = 0;

                originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
                return api(originalRequest);
              } catch (refreshError) {
                console.error('Erro ao tentar dar refresh no token:', refreshError);
                
                // Verifica se o erro é de token expirado/inválido (401, 403, ou erro específico)
                const refreshErrorStatus = refreshError.response?.status;
                const refreshErrorMessage = refreshError.response?.data?.message || '';
                
                // Se o refresh token está vencido ou inválido, desloga imediatamente
                const isTokenExpired = refreshErrorStatus === 401 || 
                                     refreshErrorStatus === 403 || 
                                     refreshErrorMessage.toLowerCase().includes('expired') ||
                                     refreshErrorMessage.toLowerCase().includes('inválido') ||
                                     refreshErrorMessage.toLowerCase().includes('invalid') ||
                                     refreshErrorMessage.toLowerCase().includes('expirado');
                
                if (isTokenExpired) {
                  console.warn('Refresh token expirado ou inválido. Fazendo logout imediatamente...');
                  logout();
                  return Promise.reject(refreshError);
                }
                
                // Se ultrapassou o limite de tentativas (para erros de rede, etc), desloga
                if (refreshTokenAttempts >= MAX_REFRESH_ATTEMPTS) {
                  console.warn('Limite de tentativas de refresh token atingido após erro. Fazendo logout...');
                  logout();
                } else {
                  // Se ainda não ultrapassou e não é erro de token expirado, apenas rejeita (pode tentar novamente na próxima requisição)
                  console.warn(`Tentativa ${refreshTokenAttempts} de refresh token falhou. Tentativas restantes: ${MAX_REFRESH_ATTEMPTS - refreshTokenAttempts}`);
                }
                
                return Promise.reject(refreshError); // Rejeita a promessa com o erro do refresh
              }
            } else {
              // Se já tentou refresh nesta requisição e ainda falhou, verifica o limite
              if (refreshTokenAttempts >= MAX_REFRESH_ATTEMPTS) {
                logout();
              }
            }
          } else {
            logout();
          }
          break;
        case 400:
        case 403:
          if (isBusinessErrorWithoutLogout || isCreateUserAlreadyExistsError || isCreateUserBusinessErrorWithoutLogout) {
            return Promise.reject(error);
          }
          logout();
          break;
        case 409:
          return Promise.resolve();
      }

      return Promise.reject(error);
    } catch (err) {
      console.error('Erro inesperado no interceptor de resposta:', err);
      return Promise.reject(err);
    }
  }
);

function logout() {
  store.dispatch('clearAuth');
  // Redireciona para login se estiver em uma rota protegida
  if (window.location.pathname.includes('/dashboard')) {
    window.location.href = '/';
  }
}

export default api;
